import React, { useEffect, useRef } from "react"
import cn from "classnames"
import type { actions } from "@/signomatic/store/actions"
import { withTranslation, type WithTranslation } from "react-i18next"
import { isTablet } from "@/common/util"
import type Sign from "@/signomatic/app/sign"
import { CheckmarkLarge, CloseLarge } from "@/skylticons"
import HelpCenter from "@/help_center/components/help_center"

export interface ToolboxItemOwnProps {
  sign?: Sign
  full?: boolean
  half?: boolean
  overflow?: boolean
  className?: string
  index?: number
  arrowTop?: number | null
  children?: React.ReactNode
  forwardedRef?: React.RefObject<HTMLDivElement> | null
  mobile?: boolean
  doneLabel?: string
}

export interface ToolboxItemStateProps {
  setToolbox: typeof actions.setToolbox
  setToolboxHeight: typeof actions.setToolboxHeight
}

type ToolboxItemProps = ToolboxItemOwnProps & ToolboxItemStateProps & WithTranslation

const ToolboxItem: React.FC<ToolboxItemProps> = ({
  children,
  full,
  half,
  className,
  overflow,
  forwardedRef,
  arrowTop,
  mobile,
  index,
  doneLabel,
  t,
  sign,
  setToolbox,
  setToolboxHeight,
}) => {
  const menurow = useRef(document.querySelector<HTMLElement>(".menurow"))
  const top = arrowTop ? arrowTop : "50%"

  const handleDone = (e: React.MouseEvent) => {
    e.stopPropagation()
    sign?.deleteEmptyObjects()

    if (isTablet() && sign) {
      setToolboxHeight(0)
      if (menurow.current) menurow.current.style.display = "flex"
    }

    setToolbox(null, index)
  }

  useEffect(() => {
    if (isTablet()) {
      setToolboxHeight(document.querySelector(".toolbox")?.clientHeight || 0)
      if (menurow.current) menurow.current.style.display = "none"
    }
  }, [setToolboxHeight])

  return (
    <div
      className={cn("toolbox visible-scrollbar", "with-done", className, { full, half, overflow })}
      ref={forwardedRef}
    >
      {mobile && (
        <div id="help-widget">
          <HelpCenter signomatic />
        </div>
      )}
      <div className="toolbox-contents z-10">{children}</div>
      <div className="button-group z-20">
        {(index || 0) >= 1 ? (
          <div
            className="toolbox-done button button-primary button-medium full-width icon-left squared mb-0"
            onClick={handleDone}
          >
            <CloseLarge />
            <span>{t("misc.close")}</span>
          </div>
        ) : (
          <div
            className="toolbox-done button button-primary button-medium full-width icon-left squared mb-0"
            onClick={handleDone}
          >
            <CheckmarkLarge />
            <span>{doneLabel || t("toolbox.done")}</span>
          </div>
        )}
      </div>
      <div
        className="absolute left-0 mt-[-8px] box-border hidden h-0 w-0 origin-[0_0] rotate-45 border-[16px] border-solid border-black border-b-white border-l-white border-r-transparent border-t-transparent shadow-[-2px_3px_3px_0_rgba(0,0,0,0.2)] content-[''] xl:block"
        style={{ top }}
      ></div>
    </div>
  )
}

export default withTranslation()(ToolboxItem)
